/**
 * Scripts card on the application detail page. Lists every script in the
 * application, grouped by component. Each row links to the script's detail
 * page where the longer fields (commandTemplate, paramSchema, warnings) are
 * editable; the row itself shows enough to scan at a glance.
 *
 * No "run" affordance yet — the run pipeline lands in a later round. The
 * card today is about defining scripts so discovery and the run flow have
 * something to operate on.
 */
import { Terminal, Zap, Clock4, ChevronRight, Loader2, GitBranch } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.js';
import { Button } from '@/components/ui/Button.js';
import { ROLE_VISUALS } from './CreateComponentDialog.js';
import type { Component, Script } from '@/api/client.js';
import { cn } from '@/lib/utils.js';

interface Props {
  applicationOrgSlug: string;
  components: Component[];
  scripts: Script[];
  loading: boolean;
  /**
   * Optional Discover handler. When provided, a "Discover from repo"
   * button renders in the card header. Omitting it (e.g. for SaaS
   * templates where repoDiscovery is false) hides the affordance — the
   * card becomes view-only.
   *
   * The manual "New script" affordance is intentionally absent — scripts
   * are now authored exclusively in `.deployit/components/<slug>/scripts.yaml`
   * and brought in via discovery (see issue #27).
   */
  onDiscover?: () => void;
}

export function ScriptsCard({
  applicationOrgSlug,
  components,
  scripts,
  loading,
  onDiscover,
}: Props): JSX.Element {
  const byComponent = new Map<string, Script[]>();
  for (const s of scripts) {
    const list = byComponent.get(s.componentId) ?? [];
    list.push(s);
    byComponent.set(s.componentId, list);
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2">
            <Terminal className="h-4 w-4" />
            Scripts
          </CardTitle>
          <CardDescription>
            Operator-runnable commands attached to components.
          </CardDescription>
        </div>
        {onDiscover && (
          <Button size="sm" variant="outline" onClick={onDiscover}>
            <GitBranch className="h-3.5 w-3.5" />
            Discover from repo
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : components.length === 0 ? (
          <p className="rounded-md border border-dashed border-border/70 bg-muted/30 px-4 py-6 text-center text-xs text-muted-foreground">
            Add a component above before defining scripts.
          </p>
        ) : scripts.length === 0 ? (
          <p className="rounded-md border border-dashed border-border/70 bg-muted/30 px-4 py-6 text-center text-xs text-muted-foreground">
            No scripts yet. Define them in{' '}
            <span className="font-mono">.deployit/components/&lt;slug&gt;/scripts.yaml</span> in
            this application&apos;s repo, then click{' '}
            <span className="font-medium">Discover from repo</span>.
          </p>
        ) : (
          <div className="space-y-5">
            {components.map((cmp) => {
              const list = byComponent.get(cmp.id) ?? [];
              if (list.length === 0) return null;
              const visual = ROLE_VISUALS[cmp.role] ?? ROLE_VISUALS.other!;
              const Icon = visual.icon;
              return (
                <section key={cmp.id} className="space-y-1.5">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                    <div className="flex h-5 w-5 items-center justify-center rounded bg-primary/10 text-primary">
                      <Icon className="h-3 w-3" />
                    </div>
                    <span>{cmp.name}</span>
                    <span className="font-mono normal-case opacity-60">/{cmp.slug}</span>
                  </div>
                  <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
                    {list.map((s) => (
                      <li key={s.id}>
                        <Link
                          to="/p/applications/scripts/$id"
                          params={{ id: s.id }}
                          search={{ org: applicationOrgSlug }}
                          className="flex items-center gap-3 px-3 py-2 hover:bg-accent/40"
                        >
                          <ExecutionBadge mode={s.executionMode} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="truncate font-medium">{s.name}</span>
                              <ReversibilityDot value={s.reversibility} />
                            </div>
                            <div className="truncate font-mono text-[11px] text-muted-foreground">
                              {s.commandTemplate}
                            </div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ExecutionBadge({ mode }: { mode: Script['executionMode'] }): JSX.Element {
  const Icon = mode === 'sync' ? Zap : Clock4;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
        mode === 'sync'
          ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
          : 'bg-sky-500/10 text-sky-700 dark:text-sky-300',
      )}
      title={mode === 'sync' ? 'Sync execution' : 'Queued execution'}
    >
      <Icon className="h-2.5 w-2.5" />
      {mode}
    </span>
  );
}

function ReversibilityDot({ value }: { value: Script['reversibility'] }): JSX.Element {
  const color =
    value === 'yes' ? 'bg-emerald-500' : value === 'best-effort' ? 'bg-amber-500' : 'bg-red-500';
  const label =
    value === 'yes' ? 'Reversible' : value === 'best-effort' ? 'Best-effort recovery' : 'Irreversible';
  return <span className={cn('h-1.5 w-1.5 rounded-full', color)} title={label} />;
}
